import classNames from 'classnames';
import { Button } from 'devextreme-react/button';
import { ScrollView } from 'devextreme-react/scroll-view';
import Toolbar, { Item as ToolbarItem } from 'devextreme-react/toolbar';
import { useScreenSize } from '../../../utils/media-query';
import { useAppSelector, useAppDispatch } from '../../../contexts/hooks';
import { setPanelPined, setPanelVisible } from '../../../contexts/slices/orders';
import { ReactNode, useEffect, useRef, useState } from 'react';

export const PanelDetails = ({ children, lockOnDesktop = false }: { children?: ReactNode; lockOnDesktop?: boolean }) => {
  const dispatch = useAppDispatch();
  const { PanelVisible, PanelPined } = useAppSelector((x) => x.globalOrders);
  const contenedorRef = useRef<HTMLDivElement>(null);
  const [localPanelVisible, setLocalPanelVisible] = useState(PanelVisible);
  const handlePined = () => {
    dispatch(setPanelPined(!PanelPined));
  };

  const { isLarge, isMedium } = useScreenSize();
  // En pantallas medianas/grandes con lockOnDesktop el panel queda fijo (no se puede ocultar ni desanclar).
  const locked = lockOnDesktop && (isLarge || isMedium);

  useEffect(() => {
    setLocalPanelVisible(locked ? true : PanelVisible);
  }, [PanelVisible, locked]);

  // Mantener el panel siempre visible y anclado cuando está bloqueado en escritorio.
  useEffect(() => {
    if (locked) {
      dispatch(setPanelVisible(true));
      dispatch(setPanelPined(true));
    }
  }, [locked, dispatch]);

  useEffect(() => {
    if (locked) return;
    if (isLarge || isMedium) {
      setLocalPanelVisible(PanelVisible);
      return;
    }
    setLocalPanelVisible(false);
    dispatch(setPanelVisible(false));
  }, []);

  const handleClickOutside = (event: MouseEvent) => {
    if (localPanelVisible && !PanelPined) {
      if (contenedorRef.current && !contenedorRef.current.contains(event.target as Node)) {
        handlePanelClose();
      }
    }
  };

  useEffect(() => {
    if (localPanelVisible && !PanelPined) {
      document.addEventListener('click', handleClickOutside);
    } else {
      document.removeEventListener('click', handleClickOutside);
    }
    // Limpia el evento cuando el componente se desmonta
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [localPanelVisible, PanelPined]);

  const handlePanelClose = () => {
    if (!PanelPined) {
      document.removeEventListener('click', handleClickOutside);
      dispatch(setPanelVisible(false));
    }
  };

  const handlePanelCloseInSide = () => {
    document.removeEventListener('click', handleClickOutside);
    dispatch(setPanelVisible(false));
  };

  return (
    <div id='contact-panel' ref={contenedorRef} className={classNames({ panel: true, open: localPanelVisible, pin: PanelPined && (isLarge || isMedium), unpin: !PanelPined })}>
      <div className='data-wrapper'>
        <Toolbar className='panel-toolbar'>
          <ToolbarItem location='before'>
            <span className='contact-name value'>Resultados</span>
          </ToolbarItem>
          <ToolbarItem location='after' visible={(isLarge || isMedium) && !locked}>
            <Button icon={PanelPined ? 'pin' : 'unpin'} onClick={handlePined} />
          </ToolbarItem>
          <ToolbarItem location='after' visible={!locked}>
            <Button icon='close' onClick={handlePanelCloseInSide} />
          </ToolbarItem>
        </Toolbar>
        <ScrollView className='panel-scroll'>{children}</ScrollView>
      </div>
    </div>
  );
};
